import { useState } from "react";

const FAQ_ITEMS = [
  {
    q: "Who does pre-purchase used car inspections near me?",
    a: "Lemon Protector provides professional pre-purchase used car inspections across New York, New Jersey, and Connecticut. We travel on-site to wherever the car is located, inspect the vehicle, and give you a direct phone consultation with your actual inspector — all starting at $139.",
  },
  {
    q: "How much does a used car inspection cost?",
    a: "Lemon Protector\u2019s pre-purchase car inspections start at $139. That price includes a full 450-point inspection, a detailed written report with photos, a free title search, a vehicle history report, and a free book value appraisal. There are no hidden fees.",
  },
  {
    q: "How long does a pre-purchase inspection take?",
    a: "A typical 450-point inspection takes approximately one to two hours on-site at the vehicle\u2019s location. After the inspection, your inspector will call you right away to walk through the findings. The written report with photos is emailed to you shortly after.",
  },
  {
    q: "Do you travel to the seller\u2019s location?",
    a: "Yes. Lemon Protector travels directly to wherever the car is located across the NY, NJ, and CT tri-state area. You don\u2019t need to move the vehicle. We coordinate the appointment time with the seller so you don\u2019t have to deal with any logistics.",
  },
  {
    q: "Is this better than a mechanic or CARFAX?",
    a: "Yes — in important ways. CARFAX only reports what has been officially filed; it cannot detect unreported accidents, flood damage, or mechanical problems. Mechanics focus on repairs and may miss or overlook issues before a sale. Lemon Protector specializes exclusively in pre-purchase inspections with zero conflict of interest. Our only job is giving you the full picture.",
  },
  {
    q: "What is included in the 450-point inspection?",
    a: "The inspection covers the entire vehicle: engine, transmission, brakes, suspension, steering, electrical systems, air conditioning and heat, body and paint condition, interior, undercarriage, and a full test drive. We also check for evidence of prior accidents, flood damage, and odometer tampering. Everything is documented in a written report with photos.",
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="bg-brand-panel py-24 lg:py-32">
      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="mb-14">
          <p className="font-sans text-xs tracking-widest uppercase text-brand-gold mb-4 flex items-center gap-3">
            <span className="w-8 h-px bg-brand-gold" />
            FAQ
          </p>
          <h2 className="font-serif text-4xl lg:text-5xl font-semibold text-brand-white leading-tight">
            Frequently Asked Questions
          </h2>
          <p className="font-sans text-sm text-brand-gray mt-4 leading-relaxed">
            Common questions from used-car buyers before scheduling a pre-purchase inspection.
          </p>
        </div>

        {/* Schema.org FAQPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: FAQ_ITEMS.map((item) => ({
                "@type": "Question",
                name: item.q,
                acceptedAnswer: { "@type": "Answer", text: item.a },
              })),
            }),
          }}
        />

        {/* Accordion */}
        <div className="flex flex-col">
          {FAQ_ITEMS.map((item, i) => (
            <div
              key={i}
              className={`border-b transition-colors ${
                i === 0 ? "border-t border-brand-gold/15" : "border-brand-gold/15"
              }`}
            >
              <button
                className="w-full text-left py-6 flex items-start justify-between gap-6 cursor-pointer group"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                <h4
                  id={`faq-${i}`}
                  className={`font-serif text-lg leading-snug transition-colors ${
                    openIndex === i ? "text-brand-gold" : "text-brand-white group-hover:text-brand-gold"
                  }`}
                >
                  <a href={`#faq-${i}`}>{item.q}</a>
                </h4>
                <div className="w-7 h-7 flex items-center justify-center shrink-0 mt-0.5">
                  <i
                    className={`text-xl text-brand-gold transition-transform duration-300 ${
                      openIndex === i ? "ri-subtract-line" : "ri-add-line"
                    }`}
                  />
                </div>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === i ? "max-h-56 pb-6" : "max-h-0"
                }`}
              >
                <p className="font-sans text-sm text-brand-gray-light leading-relaxed">{item.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
